import { ReactElement } from "react";
import { Redirect, Router, Route, Switch } from "react-router-dom";
import { Audience } from "react-ai-platform-auth";
import { ButtonSize, Loader, State } from "@telusinternationalai/seibun-ui";
import { withSplitFactory } from "@splitsoftware/splitio-react";

import { AuthProvider } from "./authHelper";
import GlobalStyle from "./components/Layout/GlobalStyle";
import ToastContainer from "./components/Toast/ToastContainer";
import { SIGNUP, SNAKE, USER_FLOW } from "./routes";
import DynamicRoutes from "./AppRoutes/DynamicRoutes";
import history from "./services/history";
import { APAPI_URL, SPLIT_IO_AUTH_KEY, TELUS_INT_ORG_ID } from "./constants";
import { useSplitContext } from "./hooks/useFeatureFlag";
import {
  SnakeForgotPasswordRedirect,
  SnakePasswordResetRedirect,
  SnakeSignInRedirect,
} from "./AppRoutes/SnakeRedirection";
import RedirectToMaintenance from "./AppRoutes/SystemMaintenanceRedirection";
import Header from "./scenes/Header";
import Footer from "./components/Layout/Footer";
import MainContainer from "./components/Layout/MainContainer";

const AUTH_CONFIG = {
  defaultLogoutRedirectUrl: SNAKE.LOGOUT,
  loginUrl: SNAKE.HOME,
  passwordResetUrl: SNAKE.RESET_PASSWORD_PAGE,
  apapiUrl: APAPI_URL ?? "",
  aud: Audience.ANNOTATION_PLATFORM,
  telusInternationalOrgId: TELUS_INT_ORG_ID ?? "",
  employeeEmailDomains: ["@telusinternational.com", "@willowtreeapps.com"],
  bioAuthUrl: SNAKE.BIOMETRIC_AUTH,
};

const splitIoSDKConfig = {
  core: {
    authorizationKey: SPLIT_IO_AUTH_KEY,
    key: "DEFAULT_USER_ID",
  },
};

const LoadingComponent = ({ title }: { title: string }): ReactElement => {
  return (
    <div className="sui-h-96">
      <State icon={<Loader role="status" />} size={ButtonSize.MEDIUM} title={title} />
    </div>
  );
};

const FeatureFlagAwaiter = ({ children }: { children: ReactElement }): ReactElement => {
  const { isReady } = useSplitContext();
  return isReady ? children : <LoadingComponent title="Configuring" />;
};

const RedirectToSignIn = (): ReactElement => <Redirect to={USER_FLOW.SIGN_IN} />;

export const Routes = (): ReactElement => {
  return (
    <Switch>
      <Route
        path="/"
        exact
        render={(): ReactElement => {
          return <Redirect to={USER_FLOW.SIGN_IN} />;
        }}
      />
      <Route exact path={USER_FLOW.SIGN_IN} component={SnakeSignInRedirect} />
      <Route exact path={USER_FLOW.RESET_PASSWORD} component={SnakePasswordResetRedirect} />
      <Route exact path={USER_FLOW.FORGOT_PASSWORD} component={SnakeForgotPasswordRedirect} />
      <Route exact path={SIGNUP.ORGANIZATION} component={RedirectToSignIn} />
      <DynamicRoutes />
    </Switch>
  );
};

export const App = (): ReactElement => {
  return (
    <>
      <GlobalStyle />
      <FeatureFlagAwaiter>
        <RedirectToMaintenance>
          <AuthProvider config={AUTH_CONFIG}>
            <Router history={history}>
              <MainContainer header={<Header />} footer={<Footer />}>
                <Routes />
              </MainContainer>
            </Router>
            <ToastContainer />
          </AuthProvider>
        </RedirectToMaintenance>
      </FeatureFlagAwaiter>
    </>
  );
};

export default withSplitFactory(splitIoSDKConfig)(App, true);
